import { Link, useLocation } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { Megaphone, Palette, Rocket, ScrollText, SlidersHorizontal, Users } from "lucide-react";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { PageHeader } from "@/components/shared/page-header";
import { cn } from "@/lib/utils";

// Alphabetical order.
interface SettingsTab {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Set when the tab hosts sub-tabs: highlight for every page beneath it. */
  matchPrefix?: string;
}

const settingsTabs: readonly SettingsTab[] = [
  { label: "Announcements", href: "/dashboard/settings/announcements", icon: Megaphone },
  {
    label: "Config",
    href: "/dashboard/settings/config/categories",
    // Config hosts sub-tabs — highlight for every /config/* page.
    matchPrefix: "/dashboard/settings/config",
    icon: SlidersHorizontal,
  },
  {
    label: "Logs",
    href: "/dashboard/settings/logs/audit",
    // Logs hosts sub-tabs — highlight for every /logs/* page.
    matchPrefix: "/dashboard/settings/logs",
    icon: ScrollText,
  },
  { label: "Releases", href: "/dashboard/settings/releases", icon: Rocket },
  { label: "Theme", href: "/dashboard/settings/theme", icon: Palette },
  { label: "Users", href: "/dashboard/settings/users", icon: Users },
];

export function SettingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  /**
   * One trail for all 21 Settings pages, derived from the tab strip below.
   *
   * None of them set breadcrumbs, so the topbar read "People & Organization ›
   * Person Directory" on People and collapsed to the bare word "Dashboard" the
   * moment you opened Settings — navigation context appearing and disappearing
   * as you moved around. Sub-navs (Config, Logs) append their own third crumb.
   */
  const activeTab = settingsTabs.find((tab) =>
    tab.matchPrefix
      ? location.pathname.startsWith(tab.matchPrefix)
      : location.pathname === tab.href,
  );
  useSetBreadcrumbs(
    activeTab
      ? [{ label: "Settings", to: "/dashboard/settings" }, { label: activeTab.label }]
      : [{ label: "Settings" }],
  );

  return (
    <>
      {/* Settings header — through PageHeader so the title matches every other
          page at every breakpoint. Hard-coding text-3xl made Settings the one
          page whose heading did not scale with the rest. */}
      <PageHeader
        className="mb-8"
        title="Settings"
        description="Manage your workspace configuration."
      />

      {/* Tab navigation */}
      <div className="relative mb-8">
        <div>
          {/* Wraps rather than scrolling: a horizontally scrolling strip hides
              tabs off the edge as Settings grows, with no affordance saying so. */}
          <nav className="flex flex-wrap gap-1">
            {settingsTabs.map((tab) => {
              const isActive = tab.matchPrefix
                ? location.pathname.startsWith(tab.matchPrefix)
                : location.pathname === tab.href;

              return (
                <Link
                  key={tab.href}
                  to={tab.href}
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    "group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-md",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/80",
                  )}
                >
                  <tab.icon
                    className={cn(
                      "h-4 w-4 transition-colors",
                      isActive
                        ? "text-primary"
                        : "text-muted-foreground/60 group-hover:text-muted-foreground",
                    )}
                  />
                  <span>{tab.label}</span>

                  {/* Active indicator — warm amber underline */}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Baseline border */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
      </div>

      {/* Tab content */}
      <div>{children}</div>
    </>
  );
}
